/* ==========================================================================
   Conquest — motion.js
   Performance-first motion:
   - Nothing is hidden before JavaScript runs, and anything already on screen
     when the page loads is never hidden at all (protects LCP and avoids flashes).
   - Reveals, image masks, the curiosity band and scale fills use
     IntersectionObserver + CSS transitions (no library).
   - GSAP + ScrollTrigger (~120KB) are loaded only when a page has a
     scroll-scrubbed effect: the product cup fill or desktop parallax.
   - Everything is skipped for visitors who ask for reduced motion.
   ========================================================================== */
(() => {
  const Conquest = (window.Conquest = window.Conquest || {});
  const root = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (!root.classList.contains('js-motion') || reduce.matches) return;

  const CSS_TARGETS = '[data-motion="reveal"], [data-motion="mask"], [data-motion="band"], [data-motion="scales"]';

  /* Arm only elements below the fold; reveal them as they approach */
  const arm = () => {
    const vh = window.innerHeight;
    const targets = Array.from(document.querySelectorAll(CSS_TARGETS)).filter((el) => !el.classList.contains('motion-armed'));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        io.unobserve(el);
        /* gentle stagger for siblings revealed together */
        const siblings = el.parentElement ? Array.from(el.parentElement.children).filter((c) => c.classList.contains('motion-armed') && !c.classList.contains('is-revealed')) : [];
        const delay = Math.min(siblings.indexOf(el), 4) * 80;
        el.style.transitionDelay = delay > 0 ? `${delay}ms` : '';
        el.classList.add('is-revealed');
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    targets.forEach((el) => {
      if (el.getBoundingClientRect().top < vh) return; /* already visible: leave untouched */
      el.classList.add('motion-armed');
      io.observe(el);
    });
  };

  const loadScript = (src) => new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src; s.async = true; s.onload = resolve; s.onerror = reject;
    document.head.appendChild(s);
  });

  const scrubbed = async () => {
    const cups = document.querySelectorAll('[data-motion="cup"]');
    const desktop = window.matchMedia('(min-width: 990px)').matches;
    const parallax = desktop ? document.querySelectorAll('[data-motion="parallax"]') : [];
    if (!cups.length && !parallax.length) return;
    try {
      if (!window.gsap) await loadScript(Conquest.assets.gsap);
      if (!window.ScrollTrigger) await loadScript(Conquest.assets.scrollTrigger);
    } catch (e) { return; }
    const { gsap, ScrollTrigger } = window;
    gsap.registerPlugin(ScrollTrigger);

    /* The at-a-glance cup fills as the profile is read (starts full without JS) */
    cups.forEach((wrap) => {
      const fill = wrap.querySelector('[data-cup-fill]');
      const crema = wrap.querySelector('[data-cup-crema]');
      const steam = wrap.querySelector('[data-cup-steam]');
      const notes = wrap.querySelectorAll('[data-cup-note]');
      if (wrap.getBoundingClientRect().top < window.innerHeight) return;
      const tl = gsap.timeline({ scrollTrigger: { trigger: wrap, start: 'top 75%', end: 'bottom 55%', scrub: 0.6 } });
      if (fill) tl.fromTo(fill, { attr: { y: 262 } }, { attr: { y: 100 }, ease: 'none' }, 0);
      if (crema) tl.fromTo(crema, { attr: { y: 262 } }, { attr: { y: 100 }, ease: 'none' }, 0);
      if (steam) tl.fromTo(steam, { opacity: 0 }, { opacity: 0.45, ease: 'none' }, 0.6);
      if (notes.length) tl.fromTo(notes, { opacity: 0, y: 12 }, { opacity: 1, y: 0, stagger: 0.12, ease: 'none' }, 0.3);
    });

    /* Restrained parallax on editorial imagery (desktop only) */
    parallax.forEach((el) => {
      const media = el.querySelector('img, .placeholder');
      if (!media) return;
      gsap.fromTo(media, { yPercent: -5, scale: 1.1 }, { yPercent: 5, scale: 1.1, ease: 'none', scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true } });
    });
  };

  const start = () => {
    arm();
    const idle = window.requestIdleCallback || ((cb) => window.setTimeout(cb, 300));
    idle(() => scrubbed(), { timeout: 2000 });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  /* Newly added sections (e.g. quick shop, facets) */
  Conquest.armMotion = arm;
})();
